import {TrustPurpose, TrustStatus} from '@sollapay/enums';

import {isFilterActive} from './filter.utils';

import type {TrustAccountFilterState} from './filter.utils';
import type {CreatedAtFilter, ListSortingState} from '@sollapay/ui/components';

/** The filter as JSON keeps it: the date range's bounds as ISO strings. */
export interface StoredTrustAccountFilterState {
  statuses: TrustStatus[];
  purposes: TrustPurpose[];
  createdAt: CreatedAtFilter;
  dateRange?: {from: string; to: string};
}

const CREATED_AT_VALUES: readonly CreatedAtFilter[] = ['all', 'this_week', '7d', '30d', '90d'];
const TRUST_STATUSES: readonly string[] = Object.values(TrustStatus);
const TRUST_PURPOSES: readonly string[] = Object.values(TrustPurpose);

export function toStoredFilterState(
  filter: TrustAccountFilterState
): StoredTrustAccountFilterState {
  const {dateRange, ...rest} = filter;
  return dateRange
    ? {...rest, dateRange: {from: dateRange.from.toISOString(), to: dateRange.to.toISOString()}}
    : rest;
}

/** What storage keeps of the filter: nothing for a cleared one, rather than the defaults. */
export function toStoredActiveFilter(
  filter: TrustAccountFilterState
): StoredTrustAccountFilterState | undefined {
  return isFilterActive(filter) ? toStoredFilterState(filter) : undefined;
}

/** What storage keeps of the sort: nothing when the list is unsorted. */
export function toStoredSorting(sorting: ListSortingState): ListSortingState | undefined {
  return sorting.length > 0 ? sorting : undefined;
}

/** A stored filter back as state, or null when a person or an older build left it malformed. */
export function parseStoredFilterState(value: unknown): TrustAccountFilterState | null {
  if (!isRecord(value)) return null;
  const {statuses, purposes, createdAt, dateRange} = value;
  if (!isListOf<TrustStatus>(statuses, TRUST_STATUSES)) return null;
  if (!isListOf<TrustPurpose>(purposes, TRUST_PURPOSES)) return null;
  if (!CREATED_AT_VALUES.some(option => option === createdAt)) return null;

  const filter: TrustAccountFilterState = {
    statuses,
    purposes,
    createdAt: createdAt as CreatedAtFilter
  };
  if (dateRange === undefined) return filter;

  const range = parseDateRange(dateRange);
  return range ? {...filter, dateRange: range} : null;
}

// Rows are grouped by status, so a status sort saved by an older build would change nothing.
const RETIRED_SORT_IDS: readonly string[] = ['status'];

/** A stored sort (one `{id, desc}` entry), or null when malformed or no longer offered. */
export function parseStoredSorting(value: unknown): ListSortingState | null {
  if (!Array.isArray(value) || value.length > 1) return null;
  const valid = value.every(
    entry =>
      isRecord(entry) &&
      typeof entry.id === 'string' &&
      typeof entry.desc === 'boolean' &&
      !RETIRED_SORT_IDS.includes(entry.id)
  );
  return valid ? (value as ListSortingState) : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isListOf<T extends string>(value: unknown, allowed: readonly string[]): value is T[] {
  return Array.isArray(value) && value.every(item => allowed.includes(item));
}

function parseDateRange(value: unknown): {from: Date; to: Date} | null {
  if (!isRecord(value) || typeof value.from !== 'string' || typeof value.to !== 'string') {
    return null;
  }
  const from = new Date(value.from);
  const to = new Date(value.to);
  return Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) ? null : {from, to};
}
